import { useAuth, useClerk, useUser } from "@clerk/clerk-react";
import {
  Cross2Icon,
  ExitIcon,
  InfoCircledIcon,
  Link2Icon,
  MagnifyingGlassIcon,
  PlusIcon,
} from "@radix-ui/react-icons";
import {
  Badge,
  Box,
  Button,
  Callout,
  Card,
  Code,
  Container,
  Dialog,
  Flex,
  Grid,
  Heading,
  IconButton,
  Link,
  Skeleton,
  Text,
  TextArea,
  TextField,
  Theme,
  VisuallyHidden,
} from "@radix-ui/themes";
import {
  FormEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  BOTTLE_FACT_HELP_TEXT,
  parseAbv,
  parseVolumeMl,
} from "./editorial/bottleFacts";
import {
  BottleDraftData,
  ContentType,
  createEditorialApiClient,
  EditorRole,
  EditorialContent,
  EditorialRecord,
  EditorialSession,
  hasClerkConfiguration,
  hasEditorialApiConfiguration,
  PublicationStatus,
  WorkflowAction,
} from "./editorial/client";
import {
  BrandMark,
  Field,
  NativeSelect,
  QueueSkeleton,
  STATUS_ORDER,
  StatusBadge,
  statusLabels,
  SummarySkeleton,
  TopBar,
} from "./editorial/ui";
import { useSystemAppearance } from "./editorial/useSystemAppearance";
import {
  DetailTarget,
  RecordDetailDialog,
} from "./editorial/RecordDetailDialog";

type EditorialItem = {
  id: string;
  type: ContentType;
  name: string;
  slug: string;
  publicationStatus: PublicationStatus;
  lastReviewed: string;
  updatedAt: string;
};

type BrandOption = {
  id: string;
  name: string;
};

type ReasonAction = Extract<WorkflowAction, "request_correction" | "rollback">;

type ReasonRequest = {
  item: EditorialItem;
  action: ReasonAction;
};

const typeLabels: Record<ContentType, string> = {
  brand: "Brand",
  bottle: "Bottle",
  guide: "Guide",
};

const actionLabels: Record<WorkflowAction, string> = {
  submit_for_review: "Submit for review",
  publish: "Publish",
  request_correction: "Request correction",
  rollback: "Roll back",
};

const actionVariants: Record<WorkflowAction, "solid" | "soft" | "outline"> = {
  submit_for_review: "soft",
  publish: "solid",
  request_correction: "outline",
  rollback: "outline",
};

const NO_BRANDS_HINT =
  "No brands yet. Create a Brand draft first, then add bottles to it.";

function toSlug(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function pluralRecords(count: number) {
  return `${count} ${count === 1 ? "record" : "records"}`;
}

function InlineNotice({ notice }: { notice: string }) {
  return (
    <Callout.Root color="gray" role="status" size="1" variant="surface">
      <Callout.Icon>
        <InfoCircledIcon />
      </Callout.Icon>
      <Callout.Text>{notice}</Callout.Text>
    </Callout.Root>
  );
}

function AuthCard({ children }: { children: ReactNode }) {
  return (
    <Flex align="center" justify="center" minHeight="100vh" px="4" py="9">
      <Box maxWidth="440px" width="100%">
        <Card size="4">
          <Flex direction="column" gap="4">
            <BrandMark />
            {children}
          </Flex>
        </Card>
      </Box>
    </Flex>
  );
}

function EditorialSignIn({
  notice,
  onSignIn,
  isSaving = false,
}: {
  notice: string;
  onSignIn?: () => void;
  isSaving?: boolean;
}) {
  return (
    <main>
      <AuthCard>
        <Flex direction="column" gap="2">
          <Heading as="h1" size="6">
            Editorial workspace
          </Heading>
          <Text as="p" color="gray" size="2">
            Sign in with an editor account to create, review, and publish
            source-backed content.
          </Text>
        </Flex>
        <Button
          disabled={!onSignIn || isSaving}
          onClick={onSignIn}
          size="3"
          type="button"
        >
          {isSaving ? "Opening sign in..." : "Sign in"}
        </Button>
        {notice && <InlineNotice notice={notice} />}
      </AuthCard>
    </main>
  );
}

function EditorialAccessRequired({
  email,
  notice,
  onSignOut,
  userId,
}: {
  email?: string | null;
  notice: string;
  onSignOut: () => void;
  userId?: string;
}) {
  return (
    <main>
      <AuthCard>
        <Flex direction="column" gap="2">
          <Heading as="h1" size="6">
            Editorial access required
          </Heading>
          <Text as="p" color="gray" size="2">
            You are signed in{email ? ` as ${email}` : ""}, but this account
            has not been assigned an editorial role.
          </Text>
          {userId && (
            <Text as="p" color="gray" size="2">
              Give this Clerk user ID to an administrator for provisioning:{" "}
              <Code>{userId}</Code>
            </Text>
          )}
        </Flex>
        {notice && <InlineNotice notice={notice} />}
        <Button
          color="gray"
          onClick={onSignOut}
          size="3"
          type="button"
          variant="soft"
        >
          <ExitIcon />
          Sign out
        </Button>
      </AuthCard>
    </main>
  );
}

function mapItem(type: ContentType, record: EditorialRecord): EditorialItem {
  return {
    id: record.id,
    type,
    name: record.name ?? record.title ?? "Untitled",
    slug: record.slug,
    publicationStatus:
      record.publicationStatus ?? record.publication_status ?? "draft",
    lastReviewed: record.lastReviewed ?? record.last_reviewed ?? "",
    updatedAt: record.updatedAt ?? record.updated_at ?? "",
  };
}

function App() {
  const appearance = useSystemAppearance();

  return (
    <Theme
      accentColor="teal"
      appearance={appearance}
      grayColor="sage"
      radius="medium"
      scaling="100%"
    >
      <EditorialApp />
    </Theme>
  );
}

function EditorialApp() {
  if (!hasEditorialApiConfiguration()) {
    return (
      <EditorialSignIn notice="Configure REACT_APP_EDITORIAL_API_URL to access the editorial workspace." />
    );
  }

  if (!hasClerkConfiguration()) {
    return (
      <EditorialSignIn notice="Configure REACT_APP_CLERK_PUBLISHABLE_KEY to access the editorial workspace." />
    );
  }

  return <EditorialWorkspace />;
}

function WorkspaceLoading() {
  return (
    <>
      <TopBar />
      <Container px="4" py="5" size="4">
        <VisuallyHidden>
          <p role="status">Loading editorial workspace...</p>
        </VisuallyHidden>
        <Flex direction="column" gap="5">
          <SummarySkeleton />
          <Grid
            align="start"
            columns={{ initial: "1", md: "minmax(0, 1fr) 400px" }}
            gap="5"
          >
            <Card size="3">
              <Skeleton height="24px" mb="2" width="160px" />
              <QueueSkeleton />
            </Card>
            <Card size="3">
              <Flex direction="column" gap="3">
                <Skeleton height="24px" width="140px" />
                <Skeleton height="32px" />
                <Skeleton height="32px" />
                <Skeleton height="32px" width="120px" />
              </Flex>
            </Card>
          </Grid>
        </Flex>
      </Container>
    </>
  );
}

function EditorialWorkspace() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const { openSignIn, signOut: clerkSignOut } = useClerk();
  const { user } = useUser();
  const api = useMemo(() => createEditorialApiClient(getToken), [getToken]);
  const [session, setSession] = useState<EditorialSession | null>(null);
  const [role, setRole] = useState<EditorRole | null>(null);
  const [items, setItems] = useState<EditorialItem[]>([]);
  const [brands, setBrands] = useState<BrandOption[]>([]);
  const [notice, setNotice] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [draftType, setDraftType] = useState<ContentType>("brand");
  const [draftName, setDraftName] = useState("");
  const [draftBrandId, setDraftBrandId] = useState("");
  const [draftAbv, setDraftAbv] = useState("");
  const [draftVolumeMl, setDraftVolumeMl] = useState("");
  const [abvError, setAbvError] = useState("");
  const [volumeMlError, setVolumeMlError] = useState("");
  const [citationTarget, setCitationTarget] = useState("");
  const [sourcePublisher, setSourcePublisher] = useState("");
  const [sourceTitle, setSourceTitle] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [sourceAccessedAt, setSourceAccessedAt] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [statusFilter, setStatusFilter] = useState<PublicationStatus | null>(
    null,
  );
  const [reasonRequest, setReasonRequest] = useState<ReasonRequest | null>(
    null,
  );
  const [isReasonOpen, setIsReasonOpen] = useState(false);
  const [reasonText, setReasonText] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [searchText, setSearchText] = useState("");
  const [detailTarget, setDetailTarget] = useState<DetailTarget | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);

  const loadWorkspace = useCallback(async () => {
    const [nextSession, content] = await Promise.all([
      api.getSession(),
      api.getContent(),
    ]);
    const nextContent = content as EditorialContent;
    const email =
      nextSession.user.email ?? user?.primaryEmailAddress?.emailAddress ?? null;

    setSession({ ...nextSession, user: { ...nextSession.user, email } });
    setRole(nextSession.role);
    setBrands(
      nextContent.brands.map((brand) => ({
        id: brand.id,
        name: brand.name ?? "Untitled",
      })),
    );
    setItems([
      ...nextContent.brands.map((record) => mapItem("brand", record)),
      ...nextContent.bottles.map((record) => mapItem("bottle", record)),
      ...nextContent.guides.map((record) => mapItem("guide", record)),
    ]);
  }, [api, user]);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    if (!isSignedIn) {
      setSession(null);
      setRole(null);
      setItems([]);
      setBrands([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setNotice("");

    loadWorkspace()
      .catch((error) => {
        if (!cancelled) {
          setNotice(error instanceof Error ? error.message : String(error));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, loadWorkspace]);

  const statusCounts = useMemo(() => {
    const counts: Record<PublicationStatus, number> = {
      draft: 0,
      in_review: 0,
      correction_required: 0,
      published: 0,
    };

    items.forEach((item) => {
      counts[item.publicationStatus] += 1;
    });

    return counts;
  }, [items]);

  const searchQuery = searchText.trim().toLowerCase();
  const isFiltered = Boolean(statusFilter || searchQuery);
  const visibleItems = items.filter(
    (item) =>
      (!statusFilter || item.publicationStatus === statusFilter) &&
      (!searchQuery ||
        item.name.toLowerCase().includes(searchQuery) ||
        item.slug.toLowerCase().includes(searchQuery)),
  );
  const detailItem = detailTarget
    ? items.find(
        (item) =>
          item.type === detailTarget.type && item.id === detailTarget.id,
      )
    : undefined;

  async function signIn() {
    setIsSaving(true);
    setNotice("");

    try {
      await openSignIn();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : String(error));
    } finally {
      setIsSaving(false);
    }
  }

  async function signOut() {
    await clerkSignOut();
    setSession(null);
    setRole(null);
    setItems([]);
    setBrands([]);
    setNotice("");
  }

  function allowedActions(item: EditorialItem): WorkflowAction[] {
    if (
      item.publicationStatus === "draft" ||
      item.publicationStatus === "correction_required"
    ) {
      return ["submit_for_review"];
    }

    if (
      item.publicationStatus === "in_review" &&
      (role === "reviewer" || role === "administrator")
    ) {
      return ["publish", "request_correction"];
    }

    if (item.publicationStatus === "published" && role === "administrator") {
      return ["rollback"];
    }

    return [];
  }

  function transition(item: EditorialItem, action: WorkflowAction) {
    if (action === "request_correction" || action === "rollback") {
      // The reason is collected in a dialog instead of window.prompt.
      setReasonRequest({ item, action });
      setReasonText("");
      setReasonError("");
      setIsReasonOpen(true);
      return;
    }

    void runTransition(item, action, null);
  }

  function submitReason(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!reasonRequest) {
      return;
    }

    if (!reasonText.trim()) {
      setReasonError("A correction or rollback needs a recorded reason.");
      return;
    }

    setIsReasonOpen(false);
    void runTransition(reasonRequest.item, reasonRequest.action, reasonText);
  }

  async function runTransition(
    item: EditorialItem,
    action: WorkflowAction,
    reason: string | null,
  ) {
    setIsSaving(true);
    setNotice("");

    try {
      await api.transition({
        contentType: item.type,
        contentId: item.id,
        action,
        reason,
      });
      await loadWorkspace();
      setDetailRefreshKey((key) => key + 1);
      setNotice(`${typeLabels[item.type]} ${action.replaceAll("_", " ")}.`);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : String(error));
    } finally {
      setIsSaving(false);
    }
  }

  async function createDraft(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const name = draftName.trim();

    if (!name) {
      setNotice("A draft needs a name or title.");
      return;
    }

    if (draftType === "bottle" && !draftBrandId) {
      setNotice("Select the related brand before creating a bottle draft.");
      return;
    }

    const abv = parseAbv(draftAbv);
    const volumeMl = parseVolumeMl(draftVolumeMl);

    if (draftType === "bottle") {
      setAbvError(abv.ok ? "" : abv.error);
      setVolumeMlError(volumeMl.ok ? "" : volumeMl.error);

      if (!abv.ok || !volumeMl.ok) {
        setNotice("Fix the ABV and volume fields before creating the draft.");
        return;
      }
    }

    setIsSaving(true);
    setNotice("");
    const slug = toSlug(name);
    const lastReviewed = new Date().toISOString().slice(0, 10);
    let data: Record<string, unknown>;

    if (draftType === "brand") {
      data = {
        name,
        slug,
        aliases: [],
        category: "To be reviewed",
        origin: "To be reviewed",
        history: "Draft awaiting editorial review.",
        editorial_status: "editorially-sourced",
        last_reviewed: lastReviewed,
      };
    } else if (draftType === "bottle") {
      const bottle: BottleDraftData = {
        brand_id: draftBrandId,
        name,
        slug,
        category: "To be reviewed",
        origin: "To be reviewed",
        description: "Draft awaiting editorial review.",
        // Blank means the source does not state it: sent as null, never 0.
        abv: abv.ok ? abv.value : null,
        volume_ml: volumeMl.ok ? volumeMl.value : null,
        editorial_status: "editorially-sourced",
        last_reviewed: lastReviewed,
      };
      data = { ...bottle };
    } else {
      data = {
        title: name,
        slug,
        summary: "Draft awaiting editorial review.",
        body: "Draft awaiting editorial review.",
        editorial_status: "editorially-sourced",
        last_reviewed: lastReviewed,
      };
    }

    try {
      await api.createDraft({ type: draftType, data });
      setDraftName("");
      setDraftBrandId("");
      setDraftAbv("");
      setDraftVolumeMl("");
      await loadWorkspace();
      setNotice(
        "Draft created. Add source citations before submitting it for review.",
      );
    } catch (error) {
      setNotice(error instanceof Error ? error.message : String(error));
    } finally {
      setIsSaving(false);
    }
  }

  async function addCitation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const target = items.find(
      (item) => `${item.type}:${item.id}` === citationTarget,
    );

    if (!target) {
      setNotice("Select an unpublished draft to cite.");
      return;
    }

    const publisher = sourcePublisher.trim();
    const title = sourceTitle.trim();
    const url = sourceUrl.trim();
    const slug = toSlug(`${publisher}-${title}`);

    if (!publisher || !title || !url || !slug) {
      setNotice("Source publisher, title, and URL are required.");
      return;
    }

    setIsSaving(true);
    setNotice("");

    try {
      await api.addCitation({
        contentType: target.type,
        contentId: target.id,
        source: {
          slug,
          publisher,
          title,
          url,
          accessedAt: sourceAccessedAt,
        },
      });
      setSourcePublisher("");
      setSourceTitle("");
      setSourceUrl("");
      await loadWorkspace();
      setNotice("Source citation added to the draft.");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : String(error));
    } finally {
      setIsSaving(false);
    }
  }

  function openDetail(item: EditorialItem) {
    setDetailTarget({ type: item.type, id: item.id, name: item.name });
    setIsDetailOpen(true);
  }

  async function handleDraftSaved() {
    await loadWorkspace();
    setNotice("Draft saved.");
  }

  function clearFilters() {
    setStatusFilter(null);
    setSearchText("");
  }

  function renderActions(item: EditorialItem) {
    const actions = allowedActions(item);

    if (actions.length === 0) {
      return null;
    }

    return actions.map((action) => (
      <Button
        color={action === "rollback" ? "gray" : undefined}
        disabled={isSaving}
        key={action}
        onClick={() => transition(item, action)}
        size="2"
        type="button"
        variant={actionVariants[action]}
      >
        {actionLabels[action]}
      </Button>
    ));
  }

  function startBrandDraft() {
    setDraftType("brand");
    document.getElementById("draft-name")?.focus();
  }

  if (isLoading) {
    return <WorkspaceLoading />;
  }

  if (isSignedIn && !session) {
    return (
      <EditorialAccessRequired
        email={user?.primaryEmailAddress?.emailAddress}
        notice={notice}
        onSignOut={signOut}
        userId={user?.id}
      />
    );
  }

  if (!session) {
    return (
      <EditorialSignIn isSaving={isSaving} notice={notice} onSignIn={signIn} />
    );
  }

  const bottleNeedsBrand = draftType === "bottle" && brands.length === 0;
  const isRollback = reasonRequest?.action === "rollback";

  return (
    <>
      <TopBar>
        <Flex align="center" gap="3" minWidth="0">
          <Box display={{ initial: "none", sm: "block" }} maxWidth="280px">
            <Text as="p" color="gray" size="2" truncate>
              {session.user.email}
            </Text>
          </Box>
          {role && (
            <Badge size={{ initial: "1", sm: "2" }} variant="soft">
              {capitalize(role)}
            </Badge>
          )}
          <Button
            color="gray"
            onClick={signOut}
            size={{ initial: "1", sm: "2" }}
            type="button"
            variant="soft"
          >
            <ExitIcon />
            Sign out
          </Button>
        </Flex>
      </TopBar>

      <Container pb="9" pt="5" px="4" size="4">
        <main>
          <Flex direction="column" gap="5">
            <section aria-label="Status summary">
              <Grid columns={{ initial: "2", sm: "4" }} gap="3">
                {STATUS_ORDER.map((status) => {
                  const isActive = statusFilter === status;

                  return (
                    <Card asChild className="gw-stat" key={status} size="2">
                      <button
                        aria-pressed={isActive}
                        onClick={() =>
                          setStatusFilter(isActive ? null : status)
                        }
                        type="button"
                      >
                        <StatusBadge status={status} />
                        <Text as="div" mt="2" size="6" weight="bold">
                          {statusCounts[status]}
                        </Text>
                      </button>
                    </Card>
                  );
                })}
              </Grid>
            </section>

            <Grid
              align="start"
              columns={{ initial: "1", md: "minmax(0, 1fr) 400px" }}
              gap="5"
            >
              <Card asChild size="3">
                <section aria-labelledby="queue-heading">
                  <Flex
                    align="center"
                    gap="3"
                    justify="between"
                    mb="2"
                    wrap="wrap"
                  >
                    <Flex align="baseline" gap="2">
                      <Heading as="h2" id="queue-heading" size="4">
                        Content queue
                      </Heading>
                      <Text color="gray" size="2">
                        {isFiltered
                          ? `${visibleItems.length} of ${pluralRecords(items.length)}`
                          : pluralRecords(items.length)}
                      </Text>
                    </Flex>
                    {statusFilter && (
                      <Button
                        onClick={() => setStatusFilter(null)}
                        size="1"
                        type="button"
                        variant="soft"
                      >
                        <Cross2Icon />
                        Clear {statusLabels[statusFilter].toLowerCase()} filter
                      </Button>
                    )}
                  </Flex>

                  {items.length > 0 && (
                    <Box mb="2" mt="3">
                      <Field htmlFor="queue-search" label="Search by name or slug">
                        <TextField.Root
                          id="queue-search"
                          onChange={(event) =>
                            setSearchText(event.target.value)
                          }
                          placeholder="e.g. baileys or orijin-rtd"
                          type="search"
                          value={searchText}
                        >
                          <TextField.Slot>
                            <MagnifyingGlassIcon height="16" width="16" />
                          </TextField.Slot>
                        </TextField.Root>
                      </Field>
                    </Box>
                  )}

                  {items.length === 0 ? (
                    <Flex
                      align="start"
                      direction="column"
                      gap="3"
                      py="6"
                    >
                      <Text as="p" size="3" weight="medium">
                        No records yet. Start with a Brand draft.
                      </Text>
                      <Text as="p" color="gray" size="2">
                        Create the draft, attach a source citation, then
                        submit it for review.
                      </Text>
                      <Button
                        onClick={startBrandDraft}
                        type="button"
                        variant="soft"
                      >
                        <PlusIcon />
                        Start a Brand draft
                      </Button>
                    </Flex>
                  ) : visibleItems.length === 0 ? (
                    <Flex align="start" direction="column" gap="3" py="6">
                      <Text as="p" size="3" weight="medium">
                        {searchQuery
                          ? `No records match "${searchText.trim()}"${
                              statusFilter
                                ? ` in ${statusLabels[statusFilter].toLowerCase()}`
                                : ""
                            }.`
                          : `No records are ${
                              statusFilter
                                ? statusLabels[statusFilter].toLowerCase()
                                : ""
                            }.`}
                      </Text>
                      <Button onClick={clearFilters} type="button" variant="soft">
                        Show all records
                      </Button>
                    </Flex>
                  ) : (
                    <ul className="gw-queue">
                      {visibleItems.map((item) => {
                        const actions = renderActions(item);

                        return (
                          <li key={`${item.type}-${item.id}`}>
                            <Flex
                              align={{ initial: "start", sm: "center" }}
                              direction={{ initial: "column", sm: "row" }}
                              gap="3"
                              justify="between"
                              py="3"
                            >
                              <Flex direction="column" gap="1" minWidth="0">
                                <Flex align="center" gap="2" wrap="wrap">
                                  <Heading as="h3" size="3" weight="medium">
                                    <Link
                                      asChild
                                      highContrast
                                      underline="hover"
                                    >
                                      <button
                                        className="gw-record-link"
                                        onClick={() => openDetail(item)}
                                        type="button"
                                      >
                                        {item.name}
                                      </button>
                                    </Link>
                                  </Heading>
                                  <StatusBadge
                                    status={item.publicationStatus}
                                  />
                                </Flex>
                                <Text color="gray" size="1">
                                  <Text weight="medium">
                                    {typeLabels[item.type]}
                                  </Text>
                                  {` · ${item.slug}`}
                                  {item.lastReviewed &&
                                    ` · Last reviewed ${item.lastReviewed}`}
                                </Text>
                              </Flex>
                              {actions && (
                                <Flex flexShrink="0" gap="2" wrap="wrap">
                                  {actions}
                                </Flex>
                              )}
                            </Flex>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </section>
              </Card>

              <Flex direction="column" gap="5">
                <Card asChild size="3">
                  <section aria-labelledby="draft-heading">
                    <Heading as="h2" id="draft-heading" mb="4" size="4">
                      Start a draft
                    </Heading>
                    <form onSubmit={createDraft}>
                      <Flex direction="column" gap="4">
                        <Field htmlFor="draft-type" label="Content type">
                          <NativeSelect
                            id="draft-type"
                            onChange={(event) =>
                              setDraftType(event.target.value as ContentType)
                            }
                            value={draftType}
                          >
                            <option value="brand">Brand</option>
                            <option value="bottle">Bottle</option>
                            <option value="guide">Guide</option>
                          </NativeSelect>
                        </Field>
                        <Field
                          htmlFor="draft-name"
                          label={draftType === "guide" ? "Guide title" : "Name"}
                        >
                          <TextField.Root
                            id="draft-name"
                            onChange={(event) =>
                              setDraftName(event.target.value)
                            }
                            required
                            value={draftName}
                          />
                        </Field>
                        {draftType === "bottle" && (
                          <Field htmlFor="draft-brand" label="Brand">
                            <NativeSelect
                              aria-describedby={
                                bottleNeedsBrand
                                  ? "draft-brand-hint"
                                  : undefined
                              }
                              id="draft-brand"
                              onChange={(event) =>
                                setDraftBrandId(event.target.value)
                              }
                              required
                              value={draftBrandId}
                            >
                              <option value="">Select a brand</option>
                              {brands.map((brand) => (
                                <option key={brand.id} value={brand.id}>
                                  {brand.name}
                                </option>
                              ))}
                            </NativeSelect>
                            {bottleNeedsBrand && (
                              <Callout.Root
                                color="amber"
                                id="draft-brand-hint"
                                mt="1"
                                size="1"
                              >
                                <Callout.Icon>
                                  <InfoCircledIcon />
                                </Callout.Icon>
                                <Callout.Text>{NO_BRANDS_HINT}</Callout.Text>
                              </Callout.Root>
                            )}
                          </Field>
                        )}
                        {draftType === "bottle" && (
                          <>
                            <Field htmlFor="draft-abv" label="ABV (%)">
                              <TextField.Root
                                aria-describedby="draft-abv-help draft-abv-error"
                                aria-invalid={abvError ? true : undefined}
                                className={abvError ? "gw-invalid" : undefined}
                                color={abvError ? "red" : undefined}
                                id="draft-abv"
                                inputMode="decimal"
                                onChange={(event) => {
                                  setDraftAbv(event.target.value);
                                  setAbvError("");
                                }}
                                placeholder="e.g. 4.5"
                                value={draftAbv}
                              />
                              <Text color="gray" id="draft-abv-help" size="1">
                                {BOTTLE_FACT_HELP_TEXT}
                              </Text>
                              {abvError && (
                                <Text
                                  color="red"
                                  id="draft-abv-error"
                                  role="alert"
                                  size="1"
                                  weight="medium"
                                >
                                  {abvError}
                                </Text>
                              )}
                            </Field>
                            <Field htmlFor="draft-volume" label="Volume (ml)">
                              <TextField.Root
                                aria-describedby="draft-volume-help draft-volume-error"
                                aria-invalid={volumeMlError ? true : undefined}
                                className={volumeMlError ? "gw-invalid" : undefined}
                                color={volumeMlError ? "red" : undefined}
                                id="draft-volume"
                                inputMode="numeric"
                                onChange={(event) => {
                                  setDraftVolumeMl(event.target.value);
                                  setVolumeMlError("");
                                }}
                                placeholder="e.g. 750"
                                value={draftVolumeMl}
                              />
                              <Text
                                color="gray"
                                id="draft-volume-help"
                                size="1"
                              >
                                {BOTTLE_FACT_HELP_TEXT}
                              </Text>
                              {volumeMlError && (
                                <Text
                                  color="red"
                                  id="draft-volume-error"
                                  role="alert"
                                  size="1"
                                  weight="medium"
                                >
                                  {volumeMlError}
                                </Text>
                              )}
                            </Field>
                          </>
                        )}
                        <Box>
                          <Button
                            aria-describedby={
                              bottleNeedsBrand ? "draft-brand-hint" : undefined
                            }
                            disabled={isSaving || bottleNeedsBrand}
                            type="submit"
                          >
                            <PlusIcon />
                            Create draft
                          </Button>
                        </Box>
                      </Flex>
                    </form>
                  </section>
                </Card>

                <Callout.Root size="2" variant="surface">
                  <Callout.Icon>
                    <InfoCircledIcon />
                  </Callout.Icon>
                  <Flex direction="column" gap="1">
                    <Heading as="h2" size="3">
                      Publish only cited, reviewed content
                    </Heading>
                    <Callout.Text>
                      Every factual profile needs a source citation and review
                      date. Never use this workspace to decide whether a
                      specific product is genuine, counterfeit, legal, illegal,
                      approved, or unsafe.
                    </Callout.Text>
                  </Flex>
                </Callout.Root>

                <Card asChild size="3">
                  <section aria-labelledby="citation-heading">
                    <Heading as="h2" id="citation-heading" mb="4" size="4">
                      Attach a source before review
                    </Heading>
                    <form onSubmit={addCitation}>
                      <Grid columns={{ initial: "1", xs: "2" }} gap="4">
                        <Box gridColumn="1 / -1">
                          <Field htmlFor="citation-draft" label="Draft">
                            <NativeSelect
                              aria-describedby="citation-draft-help"
                              id="citation-draft"
                              onChange={(event) =>
                                setCitationTarget(event.target.value)
                              }
                              required
                              value={citationTarget}
                            >
                              <option value="">
                                Select an unpublished record
                              </option>
                              {items
                                .filter(
                                  (item) =>
                                    item.publicationStatus !== "published",
                                )
                                .map((item) => (
                                  <option
                                    key={`${item.type}-${item.id}`}
                                    value={`${item.type}:${item.id}`}
                                  >
                                    {typeLabels[item.type]}: {item.name}
                                  </option>
                                ))}
                            </NativeSelect>
                            <Text
                              color="gray"
                              id="citation-draft-help"
                              size="1"
                            >
                              Published records are locked. Roll one back to
                              cite it.
                            </Text>
                          </Field>
                        </Box>
                        <Field htmlFor="citation-publisher" label="Publisher">
                          <TextField.Root
                            id="citation-publisher"
                            onChange={(event) =>
                              setSourcePublisher(event.target.value)
                            }
                            required
                            value={sourcePublisher}
                          />
                        </Field>
                        <Field htmlFor="citation-title" label="Source title">
                          <TextField.Root
                            id="citation-title"
                            onChange={(event) =>
                              setSourceTitle(event.target.value)
                            }
                            required
                            value={sourceTitle}
                          />
                        </Field>
                        <Field htmlFor="citation-url" label="Source URL">
                          <TextField.Root
                            id="citation-url"
                            onChange={(event) =>
                              setSourceUrl(event.target.value)
                            }
                            required
                            type="url"
                            value={sourceUrl}
                          />
                        </Field>
                        <Field htmlFor="citation-accessed" label="Accessed date">
                          <TextField.Root
                            id="citation-accessed"
                            onChange={(event) =>
                              setSourceAccessedAt(event.target.value)
                            }
                            required
                            type="date"
                            value={sourceAccessedAt}
                          />
                        </Field>
                        <Box gridColumn="1 / -1">
                          <Button disabled={isSaving} type="submit">
                            <Link2Icon />
                            Add citation
                          </Button>
                        </Box>
                      </Grid>
                    </form>
                  </section>
                </Card>
              </Flex>
            </Grid>
          </Flex>
        </main>
      </Container>

      <RecordDetailDialog
        actions={detailItem ? renderActions(detailItem) : null}
        api={api}
        brands={brands}
        onOpenChange={setIsDetailOpen}
        onSaved={handleDraftSaved}
        open={isDetailOpen}
        refreshKey={detailRefreshKey}
        target={detailTarget}
      />

      <Dialog.Root onOpenChange={setIsReasonOpen} open={isReasonOpen}>
        <Dialog.Content maxWidth="480px">
          <Dialog.Title>
            {isRollback ? "Roll back to draft" : "Request a correction"}
          </Dialog.Title>
          <Dialog.Description color="gray" mb="4" size="2">
            {reasonRequest
              ? `${typeLabels[reasonRequest.item.type]}: ${reasonRequest.item.name}`
              : ""}
          </Dialog.Description>
          <form noValidate onSubmit={submitReason}>
            <Field
              htmlFor="transition-reason"
              label={
                isRollback
                  ? "Why is this published record being rolled back?"
                  : "What must be corrected before this can be published?"
              }
            >
              <TextArea
                aria-describedby={
                  reasonError ? "transition-reason-error" : undefined
                }
                aria-invalid={reasonError ? true : undefined}
                className={reasonError ? "gw-invalid" : undefined}
                color={reasonError ? "red" : undefined}
                id="transition-reason"
                onChange={(event) => {
                  setReasonText(event.target.value);
                  setReasonError("");
                }}
                required
                rows={4}
                value={reasonText}
              />
              {reasonError && (
                <Text
                  color="red"
                  id="transition-reason-error"
                  role="alert"
                  size="1"
                  weight="medium"
                >
                  {reasonError}
                </Text>
              )}
            </Field>
            <Flex gap="3" justify="end" mt="4">
              <Dialog.Close>
                <Button color="gray" type="button" variant="soft">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button color={isRollback ? "red" : undefined} type="submit">
                {isRollback ? "Roll back" : "Request correction"}
              </Button>
            </Flex>
          </form>
        </Dialog.Content>
      </Dialog.Root>

      {notice &&
        // Portalled like the Radix dialogs, so a notice raised while the
        // record dialog is open sits above its overlay. The nested Theme
        // inherits the workspace theme; the key remounts it on top.
        createPortal(
          <Theme asChild hasBackground={false} key={notice}>
            <div className="gw-toast-region">
              <Flex align="start" className="gw-toast" gap="3">
                <Box className="gw-toast-icon">
                  <InfoCircledIcon />
                </Box>
                <Text as="p" role="status" size="2">
                  {notice}
                </Text>
                <IconButton
                  aria-label="Dismiss message"
                  color="gray"
                  onClick={() => setNotice("")}
                  size="1"
                  type="button"
                  variant="ghost"
                >
                  <Cross2Icon />
                </IconButton>
              </Flex>
            </div>
          </Theme>,
          document.body,
        )}
    </>
  );
}

export default App;
